// Mapping helpers extracted from legacy
// frontend/src/pages/Category/PantryCategory/hooks.ts (lines 53-235, 344-368).
// They convert the raw `/stores/:storeId/categories` payload into the
// hierarchical WebCategory[] tree the UI consumes, and the Cova
// pre-onboarding classifications payload into POSCategory[].
//
// Kept as a sibling module to useCategory.ts so the hook file stays under
// 200 lines.

import type { RawWebCategoryItem } from '../../../gateways/breadstack/categoryApis'
import type {
  GetGreenlineClassificationsResponse,
  RawGreenlineCategoryNode,
  RawPOSClassification,
  RawPOSSubClassification,
} from '../../../gateways/breadstack/posClassificationApis'
import type {
  LinkedPosCategory,
  POSCategory,
  POSSubCategory,
  WebCategory,
} from '../types'
import { assembleCategoryTree } from '../utils/categoryTree'
import { htmlDecode } from '../utils/htmlDecode'

function extractLinkedPosCategories(
  item: RawWebCategoryItem,
): LinkedPosCategory[] {
  const cova = item.cova_category ?? []
  if (cova.length) {
    return cova
      .filter((c) => !!c?.name)
      .map((c) => ({
        id: c.id,
        // Some payloads omit `is_mapped`; treat "has a name" as matchable
        // (mirrors legacy CategoryListV2 behaviour).
        isMapped: typeof c.is_mapped === 'boolean' ? c.is_mapped : true,
        name: c.name,
      }))
  }

  const greenline = item.greenline_category ?? []
  if (greenline.length) {
    return greenline
      .filter((c) => !!c?.name)
      .map((c) => ({
        id: c.id,
        isMapped: typeof c.is_mapped === 'boolean' ? c.is_mapped : true,
        name: c.name,
      }))
  }

  // Flowhub-ish shape: { category_ids: string[] }
  const categoryIds = item.pos_mapping?.category_ids
  if (Array.isArray(categoryIds)) {
    return categoryIds
      .map((id) => String(id))
      .filter(Boolean)
      .map((id) => ({
        id,
        isMapped: true,
        name: id,
      }))
  }

  return []
}

function derivePosMappingFields(
  posCategories: LinkedPosCategory[],
): Pick<WebCategory, 'posMapping' | 'posMappedName'> {
  const linked = posCategories.some((c) => c.isMapped)
  const posMappedName = posCategories
    .filter((c) => c.isMapped)
    .map((c) => c.name)
    .filter(Boolean)
    .join(', ')

  return {
    posMapping: linked ? 'mapped' : 'unmapped',
    posMappedName: posMappedName || undefined,
  }
}

function getMediaSrc(value: unknown): string | null {
  if (typeof value === 'string') return value
  if (!value || typeof value !== 'object') return null

  const src = (value as { src?: unknown; url?: unknown }).src
  if (typeof src === 'string') return src

  const url = (value as { src?: unknown; url?: unknown }).url
  return typeof url === 'string' ? url : null
}

const mapWebCategory = (item: RawWebCategoryItem): WebCategory => {
  const posCategories = extractLinkedPosCategories(item)
  const { posMapping, posMappedName } = derivePosMappingFields(posCategories)
  const seo = item.seo ?? {}

  return {
    id: String(item.id),
    // Raw parent id; normalised to the parent's string id (or undefined for
    // roots) by assembleCategoryTree().
    parentId: item.parent ?? undefined,
    depth: 0,
    displayName: htmlDecode(item.name) ?? item.name,
    slug: item.slug ?? '',
    // Empty when the API has not backfilled `full_slug` yet;
    // assembleCategoryTree() then derives it from the ancestor slugs.
    fullSlug: item.full_slug ?? '',
    // Hydrate the lock state from the backend record so reopening a drawer
    // after a save shows the persisted lock affordance. Legacy rows that
    // pre-date the field default to `false` (auto-derive from name).
    slugLocked: item.slug_locked ?? false,
    previousSlugs: [],
    redirectCount:
      typeof item.redirect_count === 'number' && item.redirect_count > 0
        ? item.redirect_count
        : 0,
    visible: typeof item.visible === 'boolean' ? item.visible : true,
    headline: item.headline ?? '',
    description: item.description ?? '',
    seoTitle: seo.title ?? '',
    seoDescription: seo.meta_description ?? '',
    products: item.count ?? 0,
    thumbnail: getMediaSrc(item.image ?? item.thumbnail),
    bannerImage: getMediaSrc(item.banner_image ?? item.bannerImage),
    posMapping,
    posMappedName,
    subcategories: [],
    posCategories,
  }
}

// Flat rows → recursive tree (max 3 levels). The flat payload carries an
// integer `parent` (0 = root); assembleCategoryTree() assigns `depth`,
// promotes orphans / over-deep rows / cycle members to roots (with a console
// warning) and sorts every level by display name.
export function buildWebCategoryTree(
  items: RawWebCategoryItem[],
): WebCategory[] {
  return assembleCategoryTree(items.map(mapWebCategory))
}

// Result of normalising a POS classifications payload: the category tree plus
// the derived total product count (summed client-side — see sumPosTotalProducts).
export interface POSClassificationsResult {
  categories: POSCategory[]
  totalProducts: number
}

// Total products across the POS tree = sum of each top-level category's `count`.
// Sub-classification counts are intentionally excluded: a parent's `count`
// already rolls up its subcategory products, so adding subs double-counts.
// The POS classifications API no longer returns an aggregate `total_products`,
// so this is derived from the per-category `count` the API does return.
export function sumPosTotalProducts(categories: POSCategory[]): number {
  return categories.reduce((sum, c) => sum + (c.count ?? 0), 0)
}

const mapPOSCategory = (
  key: string,
  item: RawPOSClassification,
): POSCategory => ({
  id: key,
  name: item.display_name,
  count: item.count ?? 0,
  sync: 'synced',
  subcategories: (item.sub_classifications ?? []).map(
    (sub: RawPOSSubClassification): POSSubCategory => ({
      id: sub.id,
      name: sub.name,
      sync: sub.is_mapped ? 'mapped' : 'unmapped',
      count: sub.count ?? 0,
    }),
  ),
})

// Cova classifications endpoint returns either an array or an object keyed
// by classification id; normalise both into a list of [key, item] tuples.
// The `display_name` typeguard below naturally drops any non-classification
// scalar sibling key (e.g. a stray `total_products`), so no special-casing is
// needed here.
function normalizeClassificationsPayload(
  data: unknown,
): [string, RawPOSClassification][] {
  if (data == null) return []
  if (Array.isArray(data)) {
    return data
      .filter(
        (row): row is RawPOSClassification =>
          row != null &&
          typeof row === 'object' &&
          'display_name' in row &&
          typeof (row as RawPOSClassification).display_name === 'string',
      )
      .map((row, i) => [`classification_${i}`, row])
  }
  if (typeof data === 'object') {
    return Object.entries(data as Record<string, RawPOSClassification>).filter(
      ([, item]) =>
        item != null &&
        typeof item === 'object' &&
        typeof item.display_name === 'string',
    )
  }
  return []
}

export function mapPOSClassifications(data: unknown): POSClassificationsResult {
  const categories = normalizeClassificationsPayload(data).map(([key, item]) =>
    mapPOSCategory(key, item),
  )
  return { categories, totalProducts: sumPosTotalProducts(categories) }
}

// Greenline POS-tree normaliser. The raw shape
// (`{ greenline_categories: { [uuid]: { id, name, sub_categories, is_mapped,
//    isActive, parentCategoryId, ... } } }`) is converted to the same internal
// `POSCategory[]` consumed by the Mapping tab + POS tab — same convention the
// Cova mapper uses (`sync: 'mapped' | 'unmapped' | 'synced'`).
//
// Defensive filters:
//   - skip nodes with `isActive === false` (legacy Greenline behaviour;
//     redundant with the backend filter but cheap to keep)
//   - sort top-level entries alphabetically by name for stable rendering
const mapGreenlineSubCategory = (
  sub: RawGreenlineCategoryNode,
): POSSubCategory => ({
  id: String(sub.id),
  name: sub.name,
  sync: sub.is_mapped ? 'mapped' : 'unmapped',
  // Per-subcategory product count from the Greenline category_mapping endpoint.
  count: sub.count ?? 0,
})

const mapGreenlineCategory = (node: RawGreenlineCategoryNode): POSCategory => ({
  id: String(node.id),
  name: node.name,
  // Per-category product count from the Greenline category_mapping endpoint.
  count: node.count ?? 0,
  sync: 'synced',
  companyId: node.companyId != null ? String(node.companyId) : undefined,
  subcategories: (node.sub_categories ?? [])
    .filter((sub) => sub?.isActive !== false)
    .map(mapGreenlineSubCategory),
})

export function mapGreenlineClassifications(
  data: GetGreenlineClassificationsResponse | null,
): POSCategory[] {
  const dict = data?.greenline_categories ?? {}
  return Object.values(dict)
    .filter(
      (node): node is RawGreenlineCategoryNode =>
        node != null &&
        typeof node === 'object' &&
        typeof node.name === 'string' &&
        node.isActive !== false,
    )
    .map(mapGreenlineCategory)
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    )
}
